import {useState, useEffect, type MutableRefObject} from 'react'

import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'

export const useCurrentVisibleHeading = (options: {ref: MutableRefObject<HTMLDivElement | null>}) => {
  const [currVisibleHeading, setCurrVisibleHeading] = useState<string | undefined>()

  const [observe, unobserve] = useIntersectionObserver(
    entries => {
      const intersecting = entries.find(entry => entry.isIntersecting)

      if (intersecting === undefined || typeof intersecting.target.id !== 'string') {
        return
      }

      setCurrVisibleHeading(intersecting.target.id)
    },
    {
      threshold: 0.75,
    },
  )

  useEffect(() => {
    if (options.ref.current === null) {
      return
    }

    /**
     * Get all h2 headings in the scope of the ref, which should be the content container.
     * Only observing h2 elements because the TOC only renders h2 headings. If we observe
     * h3/h4 elements, the active heading can become an h3/h4 id that doesn't exist in the
     * TOC, causing the TOC to lose its active highlighting when scrolling through subheadings.
     */
    const headings = Array.from(options.ref.current.querySelectorAll(':scope h2[id]'))

    for (const heading of headings) {
      observe(heading)
    }

    return () => {
      for (const heading of headings) {
        unobserve(heading)
      }
    }
  }, [options.ref, observe, unobserve])

  return currVisibleHeading
}
